import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Redirect, Switch } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTheme, useMediaQuery, makeStyles } from '@material-ui/core';

import Payment from 'pages/Account/Payment/Payment';
import Affiliate from 'pages/Account/Affiliate/Affiliate';
import Notifications from 'pages/Account/Notifications/Notifications';
import { Video } from 'pages/Account/Video/Video';
import Calendars from 'pages/Account/Calendars/Calendars';
import BillingPage from 'pages/Account/Billing/BillingPage';
import ConditionalRenderBillingPage from 'pages/Account/CohealerBilling/ConditionalRenderBillingPage';

import { ProfilePage, ClientProfilePage } from 'pages';

import AccountHeader from 'components/UI/AccountHeader';
import Can from 'components/Auth/Can';

import { UserRoles } from 'helpers/constants';

import * as userActions from 'actions/user';
import { useHeader, useRouter } from 'hooks';
import { colors } from 'utils/styles';
import MainContainer from './MainContainer';
import { TaxInfo } from '../TaxInfo/TaxInfo';
import { ROUTES } from '../../constants';
import AccountMemberships from '../../pages/Account/Memberships';
import BillingRoleSwitch from 'pages/Account/CohealerBilling/BillingRoleSwitch';

const StyledMainContainer = styled.div`
  background-color: white;
  height: 100%;
`;

const StyledMainSection = styled.div`
  padding: 30px 55px;
  ${({ mobileView }) => mobileView && `padding: 16px;`}
`;

const useStyles = makeStyles(() => ({
  billingTabBackground: {
    background: colors.lightGray,
  },
}));

function AccountContainer({ userId, getUserProfile, match: { path } }) {
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('xs'));
  useHeader('My Account');

  const { pathname } = useRouter();
  const classes = useStyles();

  useEffect(() => {
    if (userId) {
      getUserProfile(userId);
    }
  }, [userId, getUserProfile]);

  const renderCohealerRoutes = useCallback(
    () => (
      <Switch>
        <Route path={`${path}/profile`} exact component={ProfilePage} />
        <Route path={`${path}/payment`} exact component={Payment} />
        <Route path={`${path}/billing/coach`} exact component={ConditionalRenderBillingPage} />
        <Route path={`${path}/billing/coach/extended`} exact component={ConditionalRenderBillingPage} />
        <Route path={`${path}/tax-info`} exact component={TaxInfo} />
        <Route path={`${path}/notifications`} exact component={Notifications} />
        <Route path={`${path}/video`} exact component={Video} />
        <Route path={`${path}/calendars`} exact component={Calendars} />
        <Route path={`${path}/affiliate`} exact component={Affiliate} />
        <Redirect to={`${path}/profile`} />
      </Switch>
    ),
    [path],
  );
  const renderClientRoutes = useCallback(
    () => (
      <Switch>
        <Route path={`${path}/profile`} exact component={ClientProfilePage} />
        <Route path={`${path}/billing`} exact component={BillingPage} />
        <Route path={`${path}/billing/coach`} exact component={BillingRoleSwitch} />
        <Route path={`${path}/billing/coach/extended`} exact component={BillingRoleSwitch} />
        <Route path={`${path}/notifications`} exact component={Notifications} />
        <Route path={`${path}/video`} exact component={Video} />
        <Route path={`${path}/memberships`} exact component={AccountMemberships} />
        <Redirect to={`${path}/profile`} />
      </Switch>
    ),
    [path],
  );

  return (
    <MainContainer>
      <StyledMainContainer>
        <AccountHeader />
        <StyledMainSection
          mobileView={mobileView}
          className={pathname.includes(ROUTES.ACCOUNT_BILLING) && classes.billingTabBackground}
        >
          <Can roleName={UserRoles.cohealer} yes={renderCohealerRoutes} />
          <Can roleName={UserRoles.client} yes={renderClientRoutes} />
        </StyledMainSection>
      </StyledMainContainer>
    </MainContainer>
  );
}

AccountContainer.propTypes = {
  userId: PropTypes.string,
  getUserProfile: PropTypes.func.isRequired,
  match: PropTypes.shape({
    path: PropTypes.string,
    url: PropTypes.string,
    isExact: PropTypes.bool,
    params: PropTypes.object,
  }).isRequired,
};

AccountContainer.defaultProps = {
  userId: null,
};

const mapStateToProps = ({ account }) => ({
  userId: account?.user?.id,
});

const actions = {
  getUserProfile: userActions.getProfile,
};

export default connect(mapStateToProps, actions)(AccountContainer);
